/**
 * Item Management - Item Master Data List Page
 * List + FilterPanel + Export + Detail Drawer (5 Tabs)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Form, Select, Input, Button, Table, Space, Col, message, Upload, Modal, Image } from 'antd';
import type { UploadFile, UploadProps } from 'antd';
import { ExportOutlined, ImportOutlined, InboxOutlined, LoadingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import FilterPanel from '../../../../components/common/FilterPanel';
import ItemSelect from '../../../../components/common/ItemSelect';
import { useTableScrollHeight } from '@/hooks/useTableScrollHeight';
import type { ItemDTO, QueryItemPageRequest } from '../../../../types/master-data/item';
import type { Paging } from '../../../../types/common';
import * as itemService from '../../../../services/master-data/itemService';
import {
  ItemStatusOptions,
} from '../../../../constants/masterData';
import ItemDetail from '../components/ItemDetail';
import EllipsisCell from '@/components/common/EllipsisCell';

const ItemList: React.FC = () => {
  const { t } = useTranslation();
  const [searchForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<ItemDTO[]>([]);
  const [pagination, setPagination] = useState<Paging>({ currentPage: 1, pageSize: 10, totalCount: 0 });

  // Drawer state
  const [detailVisible, setDetailVisible] = useState(false);
  const [viewingCode, setViewingCode] = useState<string>('');
  const [viewingOwnerCode, setViewingOwnerCode] = useState<string>('');

  // Table body scroll height (auto-fill remaining space)
  const { tableWrapRef, scrollY } = useTableScrollHeight();

  // Import modal state
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [importLoading, setImportLoading] = useState(false);

  const uploadProps: UploadProps = {
    name: 'file',
    multiple: false,
    accept: '.xlsx,.xls,.csv',
    fileList,
    beforeUpload: (file) => {
      setFileList([file as unknown as UploadFile]);
      return false;
    },
    onRemove: () => {
      setFileList([]);
    },
  };

  const handleImportCancel = () => {
    setImportModalVisible(false);
    setFileList([]);
  };

  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.error(t('masterData.item.list.import.selectFile'));
      return;
    }

    const file = fileList[0] as unknown as File;
    setImportLoading(true);

    try {
      message.loading(t('masterData.item.list.import.uploading'), 0);
      const response = await itemService.importItemFile(file);

      if ((response as any).data?.success !== false) {
        message.destroy();
        message.success(t('masterData.item.list.import.success'));
        setImportModalVisible(false);
        setFileList([]);
        loadData(buildQueryParams(1, pagination.pageSize));

        // If backend returns a result file (e.g. error report), auto-download
        const resultData = (response as any).data?.data;
        if (resultData?.downloadUrl) {
          const link = document.createElement('a');
          link.href = resultData.downloadUrl;
          link.download = resultData.fileName || 'import_result.xlsx';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } else {
        message.destroy();
        message.error((response as any).data?.errorMessage || t('masterData.item.list.import.fail'));
      }
    } catch (err: any) {
      message.destroy();
      console.error('Import error:', err);
      message.error(err.message || t('masterData.item.list.import.failRetry'));
    } finally {
      setImportLoading(false);
    }
  };

  // Load list data
  const loadData = useCallback(async (params: QueryItemPageRequest) => {
    setLoading(true);
    try {
      const response = await itemService.pageItem(params);
      if (response.success && response.data) {
        setDataSource(response.data.tableData);
        setPagination(response.data.paging);
      } else {
        message.error(response.errorMessage || t('common.message.loadFailed'));
      }
    } catch {
      message.error(t('common.message.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Initialize
  useEffect(() => {
    loadData({ currentPage: 1, pageSize: 10 });
  }, []);

  // Build query params
  const buildQueryParams = (page: number, pageSize: number): QueryItemPageRequest => {
    const values = searchForm.getFieldsValue();
    return {
      currentPage: page,
      pageSize,
      code: values.code || undefined,
      sku: values.sku || undefined,
      category: values.category || undefined,
      brandCode: values.brandCode || undefined,
      status: values.status || undefined,
    };
  };

  // Search
  const handleSearch = () => {
    loadData(buildQueryParams(1, pagination.pageSize));
  };

  // Reset
  const handleReset = () => {
    searchForm.resetFields();
    loadData({ currentPage: 1, pageSize: pagination.pageSize });
  };

  // Page change
  const handlePageChange = (page: number, pageSize: number) => {
    loadData(buildQueryParams(page, pageSize));
  };

  // Export
  const [exportLoading, setExportLoading] = useState(false);
  const handleExport = async () => {
    setExportLoading(true);
    try {
      const values = searchForm.getFieldsValue();
      const response = await itemService.exportItem({
        status: values.status || undefined,
        code: values.code || undefined,
        brandCode: values.brandCode || undefined,
      });
      if (response.success && response.data) {
        const link = document.createElement('a');
        link.href = response.data;
        link.download = '';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        message.success(t('masterData.item.list.export.success'));
      } else {
        message.error(response.errorMessage || t('masterData.item.list.export.fail'));
      }
    } catch {
      message.error(t('masterData.item.list.export.fail'));
    } finally {
      setExportLoading(false);
    }
  };

  // View detail
  const handleViewDetail = (record: ItemDTO) => {
    setViewingCode(record.code);
    setViewingOwnerCode(record.ownerCode);
    setDetailVisible(true);
  };

  // Table columns - aligned with expected field list
  const columns = [
    {
      title: t('masterData.item.list.table.sku'),
      dataIndex: 'code',
      key: 'code',
      width: 140,
      fixed: 'left' as const,
      render: (text: string) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('masterData.item.list.table.nameTH'),
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (text: string) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('masterData.item.list.table.nameEN'),
      dataIndex: 'englishName',
      key: 'englishName',
      width: 200,
      render: (text: string) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('masterData.item.list.table.sellUnit'),
      dataIndex: 'sellUnit',
      key: 'sellUnit',
      width: 120,
      render: (text: string) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('masterData.item.list.table.barcode'),
      dataIndex: 'barcode',
      key: 'barcode',
      width: 160,
      render: (text: string) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('masterData.item.list.table.priceGF'),
      dataIndex: 'priceGf',
      key: 'priceGf',
      width: 110,
      render: (val: number | null) => <EllipsisCell>{val != null ? val.toFixed(3) : '-'}</EllipsisCell>,
    },
    {
      title: t('masterData.item.list.table.priceHY'),
      dataIndex: 'priceHy',
      key: 'priceHy',
      width: 110,
      render: (val: number | null) => <EllipsisCell>{val != null ? val.toFixed(3) : '-'}</EllipsisCell>,
    },
    {
      title: t('masterData.item.list.table.itemSoldBy'),
      dataIndex: 'itemSoldBy',
      key: 'itemSoldBy',
      width: 150,
      render: (text: string) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('masterData.item.list.table.uom'),
      dataIndex: 'uom',
      key: 'uom',
      width: 100,
      render: (text: string) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('masterData.item.list.table.currency'),
      dataIndex: 'currency',
      key: 'currency',
      width: 110,
      render: (text: string) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('masterData.item.list.table.productImage'),
      dataIndex: 'image',
      key: 'image',
      width: 160,
      render: (url: string | null, record: ItemDTO) => {
        if (!url) return '-';
        const previewSrc = record.thumbnailImage || url;
        return (
          <Image
            src={previewSrc}
            preview={{ src: url }}
            width={48}
            height={48}
            style={{ objectFit: 'cover', borderRadius: 4 }}
            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN88P/BfwAJhAPk3KFb2AAAAABJRU5ErkJggg=="
          />
        );
      },
    },
    {
      title: t('masterData.item.list.table.thumbnailImage'),
      dataIndex: 'thumbnailImage',
      key: 'thumbnailImage',
      width: 120,
      render: (url: string | null) => url ? (
        <Image
          src={url}
          width={48}
          height={48}
          style={{ objectFit: 'cover', borderRadius: 4 }}
          fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN88P/BfwAJhAPk3KFb2AAAAABJRU5ErkJggg=="
        />
      ) : '-',
    },
    {
      title: t('masterData.item.list.table.status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (text: string) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('masterData.item.list.table.statusGF'),
      dataIndex: 'productStatusGf',
      key: 'productStatusGf',
      width: 120,
      render: (text: string) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('masterData.item.list.table.statusHY'),
      dataIndex: 'productStatusHy',
      key: 'productStatusHy',
      width: 120,
      render: (text: string) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('masterData.item.list.table.brandId'),
      dataIndex: 'brandCode',
      key: 'brandCode',
      width: 130,
      render: (text: string) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('masterData.item.list.table.brandNameEn'),
      dataIndex: 'brandNameEn',
      key: 'brandNameEn',
      width: 170,
      render: (text: string) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('masterData.item.list.table.brandNameTh'),
      dataIndex: 'brandNameTh',
      key: 'brandNameTh',
      width: 170,
      render: (text: string) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('masterData.item.list.table.dimensionLength'),
      dataIndex: 'length',
      key: 'length',
      width: 140,
      render: (val: number | null) => <EllipsisCell>{val != null ? String(val) : '-'}</EllipsisCell>,
    },
    {
      title: t('masterData.item.list.table.dimensionHeight'),
      dataIndex: 'height',
      key: 'height',
      width: 140,
      render: (val: number | null) => <EllipsisCell>{val != null ? String(val) : '-'}</EllipsisCell>,
    },
    {
      title: t('masterData.item.list.table.dimensionWidth'),
      dataIndex: 'width',
      key: 'width',
      width: 140,
      render: (val: number | null) => <EllipsisCell>{val != null ? String(val) : '-'}</EllipsisCell>,
    },
    {
      title: t('masterData.item.list.table.weight'),
      dataIndex: 'weight',
      key: 'weight',
      width: 100,
      render: (val: number | null) => <EllipsisCell>{val != null ? String(val) : '-'}</EllipsisCell>,
    },
    {
      title: t('masterData.item.list.table.catGfLv1Id'),
      dataIndex: 'catGfLv1Id',
      key: 'catGfLv1Id',
      width: 160,
      render: (text: string | number) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('masterData.item.list.table.catGfLv1Name'),
      dataIndex: 'catGfLv1Name',
      key: 'catGfLv1Name',
      width: 170,
      render: (text: string) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('masterData.item.list.table.catGfLv2Id'),
      dataIndex: 'catGfLv2Id',
      key: 'catGfLv2Id',
      width: 160,
      render: (text: string | number) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('masterData.item.list.table.catGfLv2Name'),
      dataIndex: 'catGfLv2Name',
      key: 'catGfLv2Name',
      width: 170,
      render: (text: string) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('masterData.item.list.table.catGfLv3Id'),
      dataIndex: 'catGfLv3Id',
      key: 'catGfLv3Id',
      width: 160,
      render: (text: string | number) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('masterData.item.list.table.catGfLv3Name'),
      dataIndex: 'catGfLv3Name',
      key: 'catGfLv3Name',
      width: 170,
      render: (text: string) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('masterData.item.list.table.catHyLv1Id'),
      dataIndex: 'catHyLv1Id',
      key: 'catHyLv1Id',
      width: 160,
      render: (text: string | number) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('masterData.item.list.table.catHyLv1Name'),
      dataIndex: 'catHyLv1Name',
      key: 'catHyLv1Name',
      width: 170,
      render: (text: string) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('masterData.item.list.table.catHyLv2Id'),
      dataIndex: 'catHyLv2Id',
      key: 'catHyLv2Id',
      width: 160,
      render: (text: string | number) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('masterData.item.list.table.catHyLv2Name'),
      dataIndex: 'catHyLv2Name',
      key: 'catHyLv2Name',
      width: 170,
      render: (text: string) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('masterData.item.list.table.catHyLv3Id'),
      dataIndex: 'catHyLv3Id',
      key: 'catHyLv3Id',
      width: 160,
      render: (text: string | number) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('masterData.item.list.table.catHyLv3Name'),
      dataIndex: 'catHyLv3Name',
      key: 'catHyLv3Name',
      width: 170,
      render: (text: string) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('masterData.item.list.table.actions'),
      key: 'actions',
      width: 100,
      fixed: 'right' as const,
      render: (_: any, record: ItemDTO) => (
        <Space size="small">
          <Button type="link" size="small" onClick={() => handleViewDetail(record)}>
            {t('masterData.item.list.actions.detail')}
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '16px', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Filter Area */}
      <FilterPanel
        form={searchForm}
        onFilter={handleSearch}
        onReset={handleReset}
        loading={loading}
      >
        <Col span={6}>
          <Form.Item name="code" label={t('masterData.item.list.search.itemCode')}>
            <ItemSelect placeholder={t('masterData.item.list.search.placeholder.itemCode')} />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="sku" label={t('masterData.item.list.search.sku')}>
            <Input placeholder={t('masterData.item.list.search.placeholder.sku')} allowClear />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="status" label={t('masterData.item.list.search.status')}>
            <Select
              placeholder={t('masterData.item.list.search.placeholder.status')}
              allowClear
              options={ItemStatusOptions.map((opt) => ({
                label: opt.label,
                value: opt.value,
              }))}
            />
          </Form.Item>
        </Col>
      </FilterPanel>

      {/* Action Bar */}
      <div style={{ marginTop: 16, marginBottom: 16, display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
        <Button
          icon={exportLoading ? <LoadingOutlined /> : <ExportOutlined />}
          onClick={handleExport}
          loading={exportLoading}
        >
          {t('masterData.item.list.actions.export')}
        </Button>
      </div>

      {/* Table */}
      <div ref={tableWrapRef} style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Table
          rowKey="code"
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          scroll={{ x: 5430, y: scrollY }}
          pagination={{
            current: pagination.currentPage,
            pageSize: pagination.pageSize,
            total: pagination.totalCount,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showQuickJumper: true,
            showTotal: (total) => t('masterData.item.list.pagination.total', { total }),
            onChange: handlePageChange,
          }}
        />
      </div>

      {/* Detail Drawer */}
      {detailVisible && viewingCode && (
        <ItemDetail
          visible={detailVisible}
          code={viewingCode}
          ownerCode={viewingOwnerCode}
          onClose={() => setDetailVisible(false)}
          onSave={() => {
            setDetailVisible(false);
            loadData(buildQueryParams(pagination.currentPage, pagination.pageSize));
          }}
        />
      )}

      {/* Import Modal */}
      <Modal
        title={t('masterData.item.list.import.title')}
        open={importModalVisible}
        onOk={handleUpload}
        onCancel={handleImportCancel}
        confirmLoading={importLoading}
        width={600}
      >
        <div style={{ marginBottom: '16px' }}>
          <p>{t('masterData.item.list.import.description')}</p>
          <p style={{ color: '#666', fontSize: '12px' }}>
            {t('masterData.item.list.import.supportedFormats')}
          </p>
        </div>

        <Upload.Dragger {...uploadProps}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">{t('masterData.item.list.import.dragText')}</p>
          <p className="ant-upload-hint">{t('masterData.item.list.import.hint')}</p>
        </Upload.Dragger>
      </Modal>
    </div>
  );
};

export default ItemList;
